import { create, type StoreApi, type UseBoundStore } from 'zustand';
import { persist } from 'zustand/middleware';

import { DEFAULT_PAGE_SIZE } from '@/constants';

import type { OnChangeFn, SortingState } from '@tanstack/react-table';

type FilterValues = Record<string, string>;

type ListState = {
  currentPage: number;
  pageSize: number;
  setCurrentPage: (page: number | ((prevPage: number) => number)) => void;
  setPageSize: (size: number) => void;
  reset: () => void;
};

type ListStateWithQuery<TFilters extends FilterValues = Record<string, never>> = ListState & {
  sort: SortingState;
  setSort: OnChangeFn<SortingState>;
  query: string;
  filters: TFilters;
  setQuery: (query: string) => void;
  setFilter: <K extends keyof TFilters>(key: K, value: TFilters[K]) => void;
  clearFilters: () => void;
};

type ListStoreWithQueryOptions<TFilters extends FilterValues = Record<string, never>> = {
  sort?: SortingState;
  filterDefaults?: TFilters;
};

const normalizePage = (page: number) => (Number.isFinite(page) && page > 0 ? page : 1);
const normalizePageSize = (size: number) =>
  Number.isFinite(size) && size > 0 ? size : DEFAULT_PAGE_SIZE;

// pagination only
export const createListStore = (name: string) =>
  create<ListState>()(
    persist(
      (set, get) => ({
        currentPage: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        setCurrentPage: page => {
          const nextPage = typeof page === 'function' ? page(get().currentPage) : page;
          set({ currentPage: normalizePage(nextPage) });
        },
        setPageSize: size => {
          set({ pageSize: normalizePageSize(size), currentPage: 1 });
        },
        reset: () => set({ currentPage: 1, pageSize: DEFAULT_PAGE_SIZE }),
      }),
      { name }
    )
  );

// pagination + query + filters
export function createListStoreWithQuery<TFilters extends FilterValues = Record<string, never>>(
  name: string,
  options?: ListStoreWithQueryOptions<TFilters>
): UseBoundStore<StoreApi<ListStateWithQuery<TFilters>>> {
  const sortDefault: SortingState = options?.sort ?? [];
  const filterDefaults = (options?.filterDefaults ?? {}) as TFilters;

  return create<ListStateWithQuery<TFilters>>()(
    persist(
      (set, get) => ({
        currentPage: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        query: '',
        sort: sortDefault,
        filters: filterDefaults,
        setCurrentPage: page => {
          const nextPage = typeof page === 'function' ? page(get().currentPage) : page;
          set({ currentPage: normalizePage(nextPage) });
        },
        setPageSize: size => {
          set({ pageSize: normalizePageSize(size), currentPage: 1 });
        },
        setQuery: query => set({ query, currentPage: 1 }),
        setSort: updater => {
          const next = typeof updater === 'function' ? updater(get().sort) : updater;
          set({ sort: next });
        },
        setFilter: (key, value) => {
          set({
            filters: { ...get().filters, [key]: value },
            currentPage: 1,
          } as Partial<ListStateWithQuery<TFilters>>);
        },
        clearFilters: () => set({ query: '', filters: filterDefaults, currentPage: 1 }),
        reset: () =>
          set({
            currentPage: 1,
            pageSize: DEFAULT_PAGE_SIZE,
            query: '',
            sort: sortDefault,
            filters: filterDefaults,
          }),
      }),
      { name }
    )
  );
}
